import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollReveal, useStaggerReveal } from '@/hooks/useScrollReveal';

const LOGO = 'https://public.readdy.ai/ai/img_res/1641229f-de9d-42a9-901f-6e578c489111.png';

const FOOTER_COLS = [
  {
    title: 'КАТАЛОГ',
    links: [
      { label: 'Все лекарства', to: '/catalog' },
      { label: 'Витамины и БАД', to: '/catalog' },
      { label: 'Обезболивающие', to: '/catalog' },
      { label: 'Сердце и сосуды', to: '/catalog' },
      { label: 'Простуда и грипп', to: '/catalog' },
      { label: 'Антибиотики', to: '/catalog' },
    ],
  },
  {
    title: 'ПОКУПАТЕЛЯМ',
    links: [
      { label: 'Акции и скидки', to: '/promos' },
      { label: 'Программа лояльности', to: '/loyalty' },
      { label: 'Доставка и оплата', to: '/delivery' },
      { label: 'Аптеки на карте', to: '/pharmacies' },
      { label: 'Поиск лекарств', to: '/search' },
      { label: 'Личный кабинет', to: '/profile' },
    ],
  },
  {
    title: 'КОМПАНИЯ',
    links: [
      { label: 'О нас', to: '/about' },
      { label: 'Контакты', to: '/contacts' },
      { label: 'Блог о здоровье', to: '/blog' },
      { label: 'Лицензии', to: '/about' },
      { label: 'Вакансии', to: '/contacts' },
    ],
  },
];

const SOCIALS = [
  { icon: 'ri-telegram-line', href: 'https://t.me/', color: '#00f5ff', label: 'Telegram' },
  { icon: 'ri-vk-line', href: 'https://vk.com/', color: '#39ff14', label: 'ВКонтакте' },
  { icon: 'ri-youtube-line', href: 'https://youtube.com/', color: '#ff6b00', label: 'YouTube' },
  { icon: 'ri-instagram-line', href: '#', color: '#ff2d78', label: 'Instagram' },
];

const Footer = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { ref: newsletterRef, isVisible: newsletterVisible } = useScrollReveal({ threshold: 0.2 });
  const { ref: brandRef, isVisible: brandVisible } = useScrollReveal({ threshold: 0.1 });
  const { containerRef: colsRef, visibleItems: colsVisible } = useStaggerReveal(FOOTER_COLS.length, { threshold: 0.08 });
  const { ref: bottomRef, isVisible: bottomVisible } = useScrollReveal({ threshold: 0.3 });

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const body = new URLSearchParams({ email });
      await fetch('https://readdy.ai/api/form/d798a0386jhav3jpfh20', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
      setEmail('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
  <footer id="footer" className="relative" style={{ background: '#07070e' }}>
    {/* Top accent line */}
    <div className="h-px w-full" style={{ background: 'linear-gradient(90deg, transparent, #ff2d78 30%, #00f5ff 70%, transparent)' }} />

    {/* Newsletter */}
    <div className="px-6 lg:px-12 py-10 relative overflow-hidden" style={{ background: '#0a0a14' }}>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-80 h-28 opacity-15 rounded-full" style={{ background: '#ff2d78', filter: 'blur(60px)' }} />
      <div
        ref={newsletterRef as React.RefObject<HTMLDivElement>}
        className={`max-w-xl mx-auto text-center relative z-10 reveal-hidden ${newsletterVisible ? 'reveal-up' : ''}`}
      >
        <h3 className="font-orbitron font-black text-xl text-white mb-1" style={{ textShadow: '0 0 15px rgba(255,45,120,0.4)' }}>
          ПОДПИШИТЕСЬ НА АКЦИИ
        </h3>
        <p className="font-mono text-xs mb-5" style={{ color: 'rgba(255,255,255,0.35)' }}>Первыми узнавайте о скидках и новинках</p>
        {submitted ? (
          <div className="flex items-center justify-center gap-2 font-mono text-sm" style={{ color: '#39ff14' }}>
            <i className="ri-check-line" />
            Вы подписаны! Спасибо.
          </div>
        ) : (
          <form data-readdy-form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-2 max-w-md mx-auto">
            <input
              type="email"
              name="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              className="flex-1 rounded-full px-5 py-3 font-mono text-sm text-white outline-none transition-all"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,45,120,0.2)' }}
            />
            <button
              type="submit"
              disabled={submitting}
              className="whitespace-nowrap px-6 py-3 rounded-full font-orbitron font-bold text-xs tracking-widest cursor-pointer transition-all duration-300"
              style={{ background: '#ff2d78', color: '#fff', boxShadow: '0 0 15px rgba(255,45,120,0.4)', opacity: submitting ? 0.7 : 1 }}
            >
              {submitting ? '...' : 'ПОДПИСАТЬСЯ'}
            </button>
          </form>
        )}
      </div>
    </div>

    {/* Main footer */}
    <div className="px-6 lg:px-12 py-14">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 mb-12">

          {/* Brand */}
          <div
            ref={brandRef as React.RefObject<HTMLDivElement>}
            className={`lg:col-span-2 reveal-hidden ${brandVisible ? 'reveal-left' : ''}`}
          >
            <Link to="/" className="flex items-center gap-3 mb-5 cursor-pointer w-fit">
              <div className="w-10 h-10 flex items-center justify-center">
                <img src={LOGO} alt="NeonPharma" className="w-full h-full object-contain" />
              </div>
              <div>
                <div className="font-orbitron font-bold text-lg text-white">
                  NEON<span style={{ color: '#00f5ff' }}>PHARMA</span>
                </div>
                <div className="font-mono text-[10px] tracking-[0.2em]" style={{ color: 'rgba(0,245,255,0.45)' }}>ИНТЕРНЕТ-АПТЕКА</div>
              </div>
            </Link>

            <p className="font-mono text-xs leading-relaxed mb-6 max-w-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>
              Аптека нового поколения. Доставка за 2 часа, более 50 000 товаров, работаем 24/7.
            </p>

            {/* Contacts quick */}
            <div className="space-y-2 mb-6">
              <a href="tel:+78001234567" className="flex items-center gap-2 font-mono text-sm cursor-pointer transition-colors" style={{ color: 'rgba(255,255,255,0.6)' }}
                onMouseEnter={e => (e.currentTarget as HTMLElement).style.color = '#00f5ff'}
                onMouseLeave={e => (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.6)'}
              >
                <i className="ri-phone-line" style={{ color: '#00f5ff' }} />
                8 800 123-45-67
                <span className="font-mono text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(57,255,20,0.1)', color: '#39ff14', border: '1px solid rgba(57,255,20,0.2)' }}>Бесплатно</span>
              </a>
              <div className="flex items-center gap-2 font-mono text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>
                <i className="ri-time-line" style={{ color: '#f0ff00' }} />
                Работаем круглосуточно, 24/7
              </div>
            </div>

            {/* Socials */}
            <div className="flex gap-2">
              {SOCIALS.map(s => (
                <a
                  key={s.icon}
                  href={s.href}
                  rel="nofollow noopener noreferrer"
                  target="_blank"
                  aria-label={s.label}
                  className="w-9 h-9 flex items-center justify-center rounded-xl cursor-pointer transition-all duration-300"
                  style={{ background: `${s.color}10`, border: `1px solid ${s.color}25`, color: s.color }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = `0 0 12px ${s.color}60`; (e.currentTarget as HTMLElement).style.borderColor = s.color; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = 'none'; (e.currentTarget as HTMLElement).style.borderColor = `${s.color}25`; }}
                >
                  <i className={`${s.icon} text-sm`} />
                </a>
              ))}
            </div>
          </div>

          {/* Link columns */}
          <div
            ref={colsRef as React.RefObject<HTMLDivElement>}
            className="contents"
          >
            {FOOTER_COLS.map((col, idx) => (
              <div
                key={col.title}
                className={`reveal-hidden ${colsVisible[idx] ? 'reveal-up' : ''}`}
                style={{ animationDelay: `${idx * 100}ms` }}
              >
                <h4 className="font-orbitron font-bold text-xs tracking-[0.2em] mb-4" style={{ color: 'rgba(255,255,255,0.7)' }}>
                  {col.title}
                </h4>
                <ul className="space-y-2.5">
                  {col.links.map(link => (
                    <li key={link.label}>
                      <Link
                        to={link.to}
                        className="font-mono text-xs flex items-center gap-2 group transition-colors duration-200"
                        style={{ color: 'rgba(255,255,255,0.38)' }}
                        onMouseEnter={e => (e.currentTarget as HTMLElement).style.color = '#00f5ff'}
                        onMouseLeave={e => (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.38)'}
                      >
                        <span className="w-1 h-1 rounded-full shrink-0 transition-colors duration-200 group-hover:bg-neon-cyan" style={{ background: 'rgba(0,245,255,0.25)' }} />
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div className="h-px mb-6" style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.07), transparent)' }} />

        {/* Bottom bar */}
        <div
          ref={bottomRef as React.RefObject<HTMLDivElement>}
          className={`flex flex-col sm:flex-row items-center justify-between gap-4 reveal-hidden ${bottomVisible ? 'reveal-fade' : ''}`}
        >
          <p className="font-mono text-xs" style={{ color: 'rgba(255,255,255,0.2)' }}>
            &copy; 2026 NeonPharma. Все права защищены. Имеются противопоказания — проконсультируйтесь с врачом.
          </p>
          <div className="flex items-center gap-4 flex-wrap justify-center">
            {[
              { label: 'Политика конфиденциальности', to: '/contacts' },
              { label: 'Лицензия', to: '/about' },
              { label: 'Оферта', to: '/contacts' },
              { label: 'Карта сайта', to: '/sitemap' },
            ].map(l => (
              <Link
                key={l.label}
                to={l.to}
                className="font-mono text-xs transition-colors duration-200 whitespace-nowrap"
                style={{ color: 'rgba(255,255,255,0.2)' }}
                onMouseEnter={e => (e.currentTarget as HTMLElement).style.color = '#00f5ff'}
                onMouseLeave={e => (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.2)'}
              >
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>

    {/* Side accents */}
    <div className="absolute bottom-0 left-0 w-px h-32 opacity-30" style={{ background: 'linear-gradient(to top, #ff2d78, transparent)' }} />
    <div className="absolute bottom-0 right-0 w-px h-32 opacity-30" style={{ background: 'linear-gradient(to top, #00f5ff, transparent)' }} />
  </footer>
  );
};

export default Footer;
